/** One row per series with its value at the readout's moment. */
import { ChevronDown, MapPin } from "lucide-react";
import { type JSX, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUnitCategoryName } from "verdi-common/i18n";
import { Tooltip, TooltipPopup, TooltipTrigger } from "@/components/ui/tooltip";
import { useSupportedLocale } from "@/hooks/use-locale";
import { NAMESPACES } from "@/i18n/constants";
import { cn } from "@/utils/class-name";
import type { GetDisplayInfo } from "../../units/use-get-display-info";
import { INK, TEXT_SECONDARY, TEXT_TERTIARY } from "../panel-tokens";
import { groupLegend, type LegendHeading, type LegendLine } from "./legend-groups";
import { LEGEND_COLUMNS, LegendRow } from "./legend-row";
import type { ReadoutRow } from "./readout";

const MAX_LISTED_ZONES = 3;

// Top, not baseline: an inline-flex that starts with an icon takes the icon's bottom as its baseline.
const LOCATION_CLASS = "inline-flex items-center gap-[2px] align-top";

function Location({ location }: { location: NonNullable<LegendLine["location"]> }) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "readout" });
    const { t: tPicker } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    const { kind, zoneNames } = location;
    const hiddenCount = zoneNames.length - MAX_LISTED_ZONES;

    let text: string;
    if (kind === "shared") text = t("in-zones", { count: zoneNames.length });
    else if (zoneNames.length === 0) text = tPicker("no-zone");
    else {
        const shown = zoneNames.slice(0, MAX_LISTED_ZONES).join(" · ");
        text = hiddenCount > 0 ? `${shown} ${t("more-zones", { count: hiddenCount })}` : shown;
    }

    const label = (
        <>
            <MapPin aria-hidden className={"size-[10px] flex-none"} />
            {text}
        </>
    );

    // The full list on hover wherever the text doesn't already name every zone.
    if (kind === "away" && hiddenCount <= 0) return <span className={LOCATION_CLASS}>{label}</span>;

    return (
        <Tooltip>
            {/* A span, not the trigger's own button: the whole row is already a button. */}
            <TooltipTrigger
                render={<span />}
                delay={0}
                className={cn(
                    LOCATION_CLASS,
                    "underline decoration-dotted underline-offset-2 hover:text-[color:var(--location-hover)]",
                )}
                style={{ ["--location-hover" as string]: INK }}
            >
                {label}
            </TooltipTrigger>
            <TooltipPopup>
                <ul>
                    {zoneNames.map((zoneName, index) => (
                        // eslint-disable-next-line react/no-array-index-key -- two zones can share a name, and this list never reorders
                        <li key={`${index}:${zoneName}`}>{zoneName}</li>
                    ))}
                </ul>
            </TooltipPopup>
        </Tooltip>
    );
}

function LineLabel({ line, isFocused }: { line: LegendLine; isFocused: boolean }) {
    if (!line.location) return <>{line.label}</>;
    return (
        <>
            {line.label}{" "}
            <span className={"text-[11px]"} style={{ color: isFocused ? TEXT_SECONDARY : TEXT_TERTIARY }}>
                <Location location={line.location} />
            </span>
        </>
    );
}

function HeadingText({ heading, getDisplayInfo }: { heading: LegendHeading; getDisplayInfo: GetDisplayInfo }) {
    const { t: tPicker } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });

    if (heading.kind === "category") {
        const { symbol } = getDisplayInfo(heading.axisUnitCategory);
        return (
            <span className={"flex min-w-0 flex-wrap items-baseline gap-x-[6px]"}>
                <span className={"text-[12px] font-bold"} style={{ color: INK }}>
                    {heading.name}
                </span>
                {symbol && (
                    <span className={"text-[11px]"} style={{ color: TEXT_TERTIARY }}>
                        {symbol}
                    </span>
                )}
            </span>
        );
    }

    const name = heading.own ? tPicker("this-zone") : (heading.name ?? tPicker("no-zone"));
    return (
        <span className={"flex min-w-0 items-center gap-[4px]"}>
            <MapPin aria-hidden className={"size-[12px] flex-none"} style={{ color: TEXT_TERTIARY }} />
            <span className={"text-[12px] font-bold"} style={{ color: INK }}>
                {name}
            </span>
        </span>
    );
}

type ReadoutLegendProps = {
    rows: ReadoutRow[];
    getDisplayInfo: GetDisplayInfo;
    focusedStreamId: number | undefined;
    onToggleFocus: (streamId: number) => void;
};

/**
 * Headings only where the rows differ in something the panel and the card's title don't already
 * say; see legend-groups.ts for the rule.
 */
export function ReadoutLegend({
    rows,
    getDisplayInfo,
    focusedStreamId,
    onToggleFocus,
}: ReadoutLegendProps): JSX.Element | null {
    const locale = useSupportedLocale();
    const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
    if (rows.length === 0) return null;

    const toggle = (key: string) =>
        setCollapsed((previous) => {
            const next = new Set(previous);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const groups = groupLegend(rows, (unitCategory) => getUnitCategoryName(unitCategory, locale));

    return (
        <div className={"@container"}>
            <div className={cn("grid gap-x-[8px]", LEGEND_COLUMNS)}>
                {groups.map(({ key, heading, lines }) => {
                    const isCollapsed = heading !== null && collapsed.has(key);
                    // A collapsed group keeps its focused row, so the focus stays visible and clearable.
                    const shownLines = isCollapsed
                        ? lines.filter((line) => line.row.series.streamId === focusedStreamId)
                        : lines;
                    return (
                        <div
                            key={key}
                            data-headed={heading !== null}
                            className={cn(
                                "col-span-full grid grid-cols-subgrid [[data-headed=true]+&]:mt-[4px]",
                                // Extra room where headed groups follow rows with no heading.
                                heading !== null && "[[data-headed=false]+&]:mt-[12px]",
                            )}
                        >
                            {heading && (
                                <button
                                    type={"button"}
                                    aria-expanded={!isCollapsed}
                                    onClick={() => toggle(key)}
                                    className={
                                        "col-span-full flex items-center gap-[6px] py-[2px] text-left leading-[14px]"
                                    }
                                >
                                    <ChevronDown
                                        aria-hidden
                                        className={`size-[14px] flex-none transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                                        style={{ color: INK }}
                                    />
                                    <HeadingText heading={heading} getDisplayInfo={getDisplayInfo} />
                                </button>
                            )}
                            {shownLines.map((line) => {
                                const isFocused = line.row.series.streamId === focusedStreamId;
                                return (
                                    <LegendRow
                                        key={line.row.series.streamId}
                                        row={line.row}
                                        label={<LineLabel line={line} isFocused={isFocused} />}
                                        detail={line.detail}
                                        getDisplayInfo={getDisplayInfo}
                                        isFocused={isFocused}
                                        isDimmed={focusedStreamId !== undefined && !isFocused}
                                        onToggleFocus={onToggleFocus}
                                        flush={heading === null}
                                    />
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
